import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Import axios if you're using it

const ContactUs = () => {
  const [comingFromTreatment, setComingFromTreatment] = useState(false);
  const [fieldStatus, setFieldStatus] = useState({
    fullName: false,
    phoneNumber: false,
    email: false,
    lengthOfSobriety: false,
    treatmentCenter: false,
    treatmentCenterName: false,
    caseManagerName: false,
    caseManagerContact: false,
    waitingList: false,
    desiredMoveInDate: false,
  });

  const [formValues, setFormValues] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    lengthOfSobriety: '',
    desiredMoveInDate: '',
    treatmentCenter: '',
    treatmentCenterName: '',
    caseManagerName: '',
    caseManagerContact: '',
    waitingList: '',
    captchaAnswer: '',
    honeypot: '',
  });

  const [errors, setErrors] = useState({});
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [captchaError, setCaptchaError] = useState('');

  const API_ENDPOINT = 'https://qy53h4gjs4.execute-api.us-east-2.amazonaws.com/prod/post-email';

  // Generate a new CAPTCHA question
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // 1-10
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2 });
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Update field status except for captchaAnswer and honeypot
    if (fieldName !== 'captchaAnswer' && fieldName !== 'honeypot') {
      setFieldStatus((prev) => ({
        ...prev,
        [fieldName]: value.trim() !== '',
      }));
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }
    if (fieldName === 'captchaAnswer') {
      setCaptchaError('');
    }
  };

  const handleTreatmentCenterChange = (e) => {
    const value = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      treatmentCenter: value,
    }));
    setComingFromTreatment(value === 'yes');
    setFieldStatus((prev) => ({
      ...prev,
      treatmentCenter: value !== '',
    }));
    setErrors((prev) => ({
      ...prev,
      treatmentCenter: '',
    }));
  };

  const validateFields = () => {
    const newErrors = {};
    if (!fieldStatus.fullName) newErrors.fullName = 'Please fill out this field.';
    if (!fieldStatus.phoneNumber) newErrors.phoneNumber = 'Please fill out this field.';
    if (!fieldStatus.email) newErrors.email = 'Please fill out this field.';
    if (!fieldStatus.lengthOfSobriety) newErrors.lengthOfSobriety = 'Please fill out this field.';
    if (!fieldStatus.treatmentCenter) newErrors.treatmentCenter = 'Please select an option.';
    if (comingFromTreatment) {
      if (!fieldStatus.treatmentCenterName)
        newErrors.treatmentCenterName = 'Please fill out this field.';
      if (!fieldStatus.caseManagerName)
        newErrors.caseManagerName = 'Please fill out this field.';
      if (!fieldStatus.caseManagerContact)
        newErrors.caseManagerContact = 'Please fill out this field.';
    }
    if (!fieldStatus.waitingList) newErrors.waitingList = 'Please select an option.';
    if (!fieldStatus.desiredMoveInDate)
      newErrors.desiredMoveInDate = 'Please fill out this field.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCaptcha = () => {
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(formValues.captchaAnswer, 10) !== correctAnswer) {
      setCaptchaError('Incorrect answer to the CAPTCHA.');
      return false;
    }
    return true;
  };

  const validateHoneypot = () => {
    if (formValues.honeypot) {
      // If honeypot field is filled, it's likely a bot
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Validate honeypot
    if (!validateHoneypot()) {
      // Optionally, you can silently fail or display a generic error
      alert('Submission failed.');
      return;
    }


    if (!validateFields()) {
      return;
    }


    if (!validateCaptcha()) {
      return;
    }

    
    const { captchaAnswer, honeypot, ...submissionData } = formValues;

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        alert('Form submitted successfully!');
        // Reset form
        setFormValues({
          fullName: '',
          phoneNumber: '',
          email: '',
          lengthOfSobriety: '',
          desiredMoveInDate: '',
          treatmentCenter: '',
          treatmentCenterName: '',
          caseManagerName: '',
          caseManagerContact: '',
          waitingList: '',
          captchaAnswer: '',
          honeypot: '',
        });
        setFieldStatus({
          fullName: false,
          phoneNumber: false,
          email: false,
          lengthOfSobriety: false,
          treatmentCenter: false,
          treatmentCenterName: false,
          caseManagerName: false,
          caseManagerContact: false,
          waitingList: false,
          desiredMoveInDate: false,
        });
        setCaptchaError('');
        generateCaptcha();
      } else {
        const errorData = await response.json();
        alert(`There was an error submitting the form: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error submitting the form:', error);
      alert('There was an error submitting the form.');
    }
  };

  return (
    <section id="contact-us" className="py-12">
      <div className="max-w-3xl mx-auto px-4" data-aos="fade-up">
        <h2 className="text-4xl font-heading font-bold text-primary mb-6 text-center">
          Contact Us
        </h2>
        <p className="text-xl mb-8 text-center">
          Leave us your information below, and we will be in touch!
        </p>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="hidden">
            <label htmlFor="honeypot"></label>
            <input
              type="text"
              id="honeypot"
              name="honeypot"
              value={formValues.honeypot}
              onChange={(e) => handleInputChange(e, 'honeypot')}
              autoComplete="off"
            />
          </div>

          {/* Full Name */}
          <div className="relative">
            <label htmlFor="full-name" className="block text-gray-700 font-bold mb-2">
              Full Name{' '}
              {!fieldStatus.fullName ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <input
              type="text"
              id="full-name"
              name="full-name"
              value={formValues.fullName}
              className={`w-full p-4 bg-gray-100 border ${
                errors.fullName ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'fullName')}
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>
          {/* Phone Number */}
          <div className="relative">
            <label htmlFor="phone-number" className="block text-gray-700 font-bold mb-2">
              Phone Number{' '}
              {!fieldStatus.phoneNumber ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <input
              type="tel"
              id="phone-number"
              name="phone-number"
              value={formValues.phoneNumber}
              className={`w-full p-4 bg-gray-100 border ${
                errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'phoneNumber')}
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
            )}
          </div>
          {/* Email */}
          <div className="relative">
            <label htmlFor="email" className="block text-gray-700 font-bold mb-2">
              Email{' '}
              {!fieldStatus.email ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formValues.email}
              className={`w-full p-4 bg-gray-100 border ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'email')}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          {/* Length of Sobriety */}
          <div className="relative">
            <label htmlFor="length-of-sobriety" className="block text-gray-700 font-bold mb-2">
              Length of Sobriety{' '}
              {!fieldStatus.lengthOfSobriety ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <input
              type="text"
              id="length-of-sobriety"
              name="length-of-sobriety"
              value={formValues.lengthOfSobriety}
              className={`w-full p-4 bg-gray-100 border ${
                errors.lengthOfSobriety ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'lengthOfSobriety')}
            />
            {errors.lengthOfSobriety && (
              <p className="text-red-500 text-sm mt-1">{errors.lengthOfSobriety}</p>
            )}
          </div>
          {/* Desired Move-In Date */}
          <div className="relative">
            <label htmlFor="desired-move-in-date" className="block text-gray-700 font-bold mb-2">
              Desired Move-In Date{' '}
              {!fieldStatus.desiredMoveInDate ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <input
              type="date"
              id="desired-move-in-date"
              name="desired-move-in-date"
              value={formValues.desiredMoveInDate}
              className={`w-full p-4 bg-gray-100 border ${
                errors.desiredMoveInDate ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'desiredMoveInDate')}
            />
            {errors.desiredMoveInDate && (
              <p className="text-red-500 text-sm mt-1">{errors.desiredMoveInDate}</p>
            )}
          </div>
          {/* Treatment Center */}
          <div className="relative">
            <label htmlFor="treatment-center" className="block text-gray-700 font-bold mb-2">
              Will you be coming directly from a treatment center?{' '}
              {!fieldStatus.treatmentCenter ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <select
              id="treatment-center"
              name="treatment-center"
              value={formValues.treatmentCenter}
              className={`w-full p-4 bg-gray-100 border ${
                errors.treatmentCenter ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={handleTreatmentCenterChange}
            >
              <option value="" disabled hidden>
                Select an option
              </option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {errors.treatmentCenter && (
              <p className="text-red-500 text-sm mt-1">{errors.treatmentCenter}</p>
            )}
          </div>
          {/* Additional Fields for "Yes" */}
          {comingFromTreatment && (
            <>
              <div className="relative">
                <label
                  htmlFor="treatment-center-name"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Name of Treatment Center{' '}
                  {!fieldStatus.treatmentCenterName ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-green-500 ml-2">✔</span>
                  )}
                </label>
                <input
                  type="text"
                  id="treatment-center-name"
                  name="treatment-center-name"
                  value={formValues.treatmentCenterName}
                  className={`w-full p-4 bg-gray-100 border ${
                    errors.treatmentCenterName ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:border-secondary`}
                  onChange={(e) => handleInputChange(e, 'treatmentCenterName')}
                />
                {errors.treatmentCenterName && (
                  <p className="text-red-500 text-sm mt-1">{errors.treatmentCenterName}</p>
                )}
              </div>
              <div className="relative">
                <label htmlFor="case-manager-name" className="block text-gray-700 font-bold mb-2">
                  Case Manager Name{' '}
                  {!fieldStatus.caseManagerName ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-green-500 ml-2">✔</span>
                  )}
                </label>
                <input
                  type="text"
                  id="case-manager-name"
                  name="case-manager-name"
                  value={formValues.caseManagerName}
                  className={`w-full p-4 bg-gray-100 border ${
                    errors.caseManagerName ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:border-secondary`}
                  onChange={(e) => handleInputChange(e, 'caseManagerName')}
                />
                {errors.caseManagerName && (
                  <p className="text-red-500 text-sm mt-1">{errors.caseManagerName}</p>
                )}
              </div>
              <div className="relative">
                <label
                  htmlFor="case-manager-contact"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Case Manager Contact Number{' '}
                  {!fieldStatus.caseManagerContact ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-green-500 ml-2">✔</span>
                  )}
                </label>
                <input
                  type="tel"
                  id="case-manager-contact"
                  name="case-manager-contact"
                  value={formValues.caseManagerContact}
                  className={`w-full p-4 bg-gray-100 border ${
                    errors.caseManagerContact ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:border-secondary`}
                  onChange={(e) => handleInputChange(e, 'caseManagerContact')}
                />
                {errors.caseManagerContact && (
                  <p className="text-red-500 text-sm mt-1">{errors.caseManagerContact}</p>
                )}
              </div>
            </>
          )}
          {/* Waiting List */}
          <div className="relative">
            <label htmlFor="waiting-list" className="block text-gray-700 font-bold mb-2">
              Are you willing to be placed on a waiting list?{' '}
              {!fieldStatus.waitingList ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-green-500 ml-2">✔</span>
              )}
            </label>
            <select
              id="waiting-list"
              name="waiting-list"
              value={formValues.waitingList}
              className={`w-full p-4 bg-gray-100 border ${
                errors.waitingList ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:border-secondary`}
              onChange={(e) => handleInputChange(e, 'waitingList')}
            >
              <option value="" disabled hidden>
                Select an option
              </option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {errors.waitingList && (
              <p className="text-red-500 text-sm mt-1">{errors.waitingList}</p>
            )}
          </div>
          {/* Custom CAPTCHA */}
          <div className="relative">
            <label htmlFor="captcha" className="block text-gray-700 font-bold mb-2">
              To verify you're human, please solve the following:
            </label>
            <div className="flex items-center mb-2">
              <span className="text-gray-800 mr-2">
                What is {captcha.num1} + {captcha.num2}?
              </span>
              <input
                type="number"
                id="captcha"
                name="captcha"
                value={formValues.captchaAnswer}
                className={`w-20 p-2 bg-gray-100 border ${
                  captchaError ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:outline-none focus:border-secondary`}
                onChange={(e) => handleInputChange(e, 'captchaAnswer')}
              />
            </div>
            {captchaError && (
              <p className="text-red-500 text-sm mt-1">{captchaError}</p>
            )}
          </div>
          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="px-8 py-3 bg-secondary text-white rounded-full text-lg font-medium hover:bg-red-600 transition duration-300"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactUs;
